import { useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidDocumentProcessingStatusSubscription,
  BidDocumentProcessingStatusSubscriptionVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { useSubscription } from "../../useGraphQlClient";

// adding this so it generates the types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _bidDocumentProcessingStatusSubscription = graphql(`
  subscription bidDocumentProcessingStatus($bidId: String!) {
    bidDocumentProcessingStatus(bidId: $bidId) {
      status
      output {
        fileName
        documentId
      }
    }
  }
`);

type UseBidDocumentProcessingStatusProps = {
  bidId?: string;
  onSuccess: (data: BidDocumentProcessingStatusSubscription) => void;
  onError: (data: BidDocumentProcessingStatusSubscription) => void;
  enabled?: boolean;
};

export function useBidDocumentProcessingStatus({
  bidId,
  onSuccess,
  onError,
  enabled = true,
}: UseBidDocumentProcessingStatusProps) {
  const client = useQueryClient();

  const { logEvents } = useTracking();

  const { data, ...rest } = useSubscription<
    BidDocumentProcessingStatusSubscription,
    BidDocumentProcessingStatusSubscriptionVariables
  >(
    `
    subscription bidDocumentProcessingStatus($bidId: String!) {
      bidDocumentProcessingStatus(bidId: $bidId) {
        status
        output {
          fileName
          documentId
        }
      }
    }
`,
    {
      bidId: bidId || "",
    },
    {
      enabled: !!bidId && enabled,
      onData: (data) => {
        if (data.bidDocumentProcessingStatus.status === "success" && bidId) {
          logEvents({
            name: EventNames.bidDocumentProcessedSuccessfully,
            data: {
              bidId,
              fileName: data.bidDocumentProcessingStatus.output?.fileName,
            },
          });
          void client.invalidateQueries(["bidDocuments", bidId]);
          console.log(
            `Bid document processing successful for file: ${data.bidDocumentProcessingStatus.output?.fileName}`,
          );

          onSuccess(data);
        } else {
          logEvents({
            name: EventNames.bidDocumentProcessingError,
            data: {
              bidId,
              error: "An error occurred while processing the bid document.",
            },
          });
          onError(data);
        }
      },
    },
  );
  return { data, ...rest };
}
